import { Button, Logo, Text } from '@change/ds-core-ui';
import { Form } from '@change/ds-forms';
import { EditOutlineIcon } from '@change/ds-icons';

import { TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { Loading } from '../PersonalPrompt/components/Loading';
import { CtaSection } from '../shared/entryPages/CtaSection';
import { DecisionMakerSection } from '../shared/entryPages/infoSections/DecisionMakerSection';
import { VoiceHeardSection } from '../shared/entryPages/infoSections/VoiceHeardSection';

import { EntryField } from './components/EntryField';
import { useSocialEntry } from './hooks/useSocialEntry';

/**
 * Landing step for social starter with prefilled issue and optionally prefilled
 * personal story. Displays the prefilled content and allows user to edit as needed.
 * The CTA submits the prefilled draft to trigger AI draft
 * generation and advance to the preview step.
 */
export const SocialEntry: React.FC = () => {
	const { translate } = useI18n();
	const { data, actions } = useSocialEntry();

	if (data.loading) return <Loading />;

	return (
		<div className="flex flex-col" data-qa="social-entry">
			<div className="bg-linear-to-b from-[#F0F7FF] to-white pt-8 md:pt-20">
				<div className="container mx-auto flex max-w-150 flex-col gap-10 md:gap-17">
					<div className="flex flex-col items-center gap-4 px-4 text-center md:px-0">
						<Logo variant="symbol" size="large" />

						<Text as="h1" variant="heading" className="pt-2">
							<TranslateHtml value="corgi.start_a_petition.chat_app_entry.social_entry.heading" />
						</Text>
						<Text as="p" size="large">
							<TranslateHtml value="corgi.start_a_petition.chat_app_entry.description" />
						</Text>
					</div>

					<Form
						onSubmit={actions.submit}
						schema={data.schema}
						defaultValues={data.defaultValues}
						className="flex flex-col items-center gap-12"
						data-qa="social-entry-form"
					>
						<EntryField
							name="issue"
							title={translate('corgi.start_a_petition.chat_app_entry.social_entry.issue_title')}
							subtitle={translate('corgi.start_a_petition.chat_app_entry.social_entry.issue_subtitle')}
							label={translate('corgi.start_a_petition.chat_app_entry.social_entry.issue_title')}
							onBlur={actions.persistIssueDraft}
						/>

						<EntryField
							name="personalStory"
							title={translate('corgi.start_a_petition.chat_app_entry.social_entry.personal_story_title')}
							subtitle={translate('corgi.start_a_petition.chat_app_entry.social_entry.personal_story_subtitle')}
							label={translate('corgi.start_a_petition.chat_app_entry.social_entry.personal_story_title')}
							placeholder={translate(
								'corgi.start_a_petition.chat_app_entry.social_entry.personal_story_input_placeholder',
							)}
							onBlur={actions.persistPersonalStoryDraft}
						/>

						<Button type="submit" variant="primary" leadingIcon={<EditOutlineIcon />} data-qa="social-entry-cta-button">
							{translate('corgi.start_a_petition.chat_app_entry.social_entry.cta_button')}
						</Button>
					</Form>
				</div>
			</div>

			<div className="pb-20">
				<VoiceHeardSection />
				<DecisionMakerSection />
			</div>

			<CtaSection
				icon={<EditOutlineIcon />}
				onCtaClick={actions.submitFromFooterCta}
				onCtaView={actions.trackCtaSectionView}
				headingText={<TranslateHtml value="corgi.start_a_petition.chat_app_entry.cta_heading" />}
				buttonText={translate('corgi.start_a_petition.chat_app_entry.social_entry.cta_button')}
				qaId="social-entry-footer-cta-button"
			/>
		</div>
	);
};
